import React, { useMemo, useState } from 'react';
import { Alert, Badge, Button, Col, Form, Modal, Row, Table } from 'react-bootstrap';

import ReasonModal from './ReasonModal';
import jellyIcon from '../../assets/bonanza-jelly-icon.png';
import { showUtcClock } from '../../utils/utcDateTime';

// The sybil review surface (W11, design: wiki/waitlist-operator-design.md
// section 2). Observe everything, block nothing at entry: review happens
// HERE, after the fact. Freeze is the ONLY hold and it spans the WHOLE
// waitlist (Admin's ruling 2026-09-03): every close excludes frozen
// entries until unfrozen; there is no operator revoke anywhere. Every lever demands a
// reason via the modal — the ADJ audit row is the record.
// The CSV is Admin's offline pipeline: attribution, checkmarks, coins,
// fruit, fingerprint hashes; clustering intelligence stays HIS, offline.
const STATE_BADGE = { active: 'success', frozen: 'info', revoked: 'danger' };

const short = value => (value?.length > 12 ? `${value.slice(0, 4)}…${value.slice(-4)}` : value);

const copyText = async text => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const scratch = document.createElement('textarea');
  scratch.value = text;
  document.body.appendChild(scratch);
  scratch.select();
  document.execCommand('copy');
  scratch.remove();
};

// Click-to-copy for truncated ids (Gwen 2026-09-02): the cell shows 4…4 but
// a click puts the FULL value on the clipboard — the bubble confirms it.
// Account and wallet both get it; the full value also rides the hover title.
export const CopyShort = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  if (!value) return '—';
  const copy = async () => {
    try {
      await copyText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      // clipboard blocked — hover still shows the full value via title
    }
  };
  return (
    <span className="copy-short">
      <span
        role="button"
        tabIndex={0}
        title={value}
        aria-label={`copy ${label} ${value}`}
        onClick={copy}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            copy();
          }
        }}
      >
        {short(value)}
      </span>
      {copied && <span className="copy-short-bubble">copied!</span>}
    </span>
  );
};

/** GRANT JELLY (Admin via Gwen, 2026-09-04): banded bonus drops — each band
 *  carries its own amount, wallet list, and a player-visible title. One
 *  batchId rides every chunk, so a retried submit can never double-drop.
 *  Icon slot: Gwen supplies the jelly PNG; text-only until then. */
const GRANT_COINS_MAX = 10000;
const GRANT_CHUNK = 1000;

const emptyBand = () => ({ coins: '', title: '', walletsRaw: '' });

const newBatchId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `grant-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

// Chunking is deterministic (same bands, same chunks), so a retry from
// batch k re-sends exactly the wallets batch k held the first time.
const chunkBands = bands => {
  const chunks = [];
  let current = [];
  let count = 0;
  for (const band of bands) {
    let rest = band.walletIds;
    while (rest.length > 0) {
      const room = GRANT_CHUNK - count;
      const take = rest.slice(0, room);
      rest = rest.slice(room);
      current.push({ coins: band.coins, title: band.title, walletIds: take });
      count += take.length;
      if (count === GRANT_CHUNK) {
        chunks.push(current);
        current = [];
        count = 0;
      }
    }
  }
  if (current.length > 0) chunks.push(current);
  return chunks;
};

const GrantJellyModal = ({ show, onSubmit, onHide }) => {
  const [bands, setBands] = useState([emptyBand()]);
  const [parseError, setParseError] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [batchNote, setBatchNote] = useState(null);

  const reset = () => {
    setBands([emptyBand()]);
    setParseError(null);
    setConfirming(null);
    setResult(null);
    setBatchNote(null);
  };
  const close = () => {
    reset();
    onHide();
  };

  const setBand = (index, patch) =>
    setBands(bands.map((band, i) => (i === index ? { ...band, ...patch } : band)));

  const review = () => {
    setParseError(null);
    const parsed = [];
    const seen = new Map(); // wallet -> the band that already claims it
    for (const [index, band] of bands.entries()) {
      const coins = Number(band.coins);
      if (!Number.isInteger(coins) || coins < 1 || coins > GRANT_COINS_MAX) {
        return setParseError(
          `Band ${index + 1}: the amount must be a whole number between 1 and ${GRANT_COINS_MAX}.`
        );
      }
      const title = band.title.trim();
      if (title.length === 0 || title.length > 80) {
        return setParseError(`Band ${index + 1}: a title of 1-80 characters is required.`);
      }
      let wallets;
      try {
        wallets = JSON.parse(band.walletsRaw);
      } catch {
        return setParseError(`Band ${index + 1}: not valid JSON. Expected ["wallet1", "wallet2"]`);
      }
      if (
        !Array.isArray(wallets) ||
        wallets.length === 0 ||
        !wallets.every(w => typeof w === 'string')
      ) {
        return setParseError(`Band ${index + 1}: expected a JSON list of wallet strings.`);
      }
      // One wallet, one band: the server keeps only the FIRST drop per
      // wallet and batch, so a wallet in two bands would silently lose the
      // second amount and show up as a nameless duplicate.
      for (const wallet of wallets) {
        const owner = seen.get(wallet);
        if (owner !== undefined) {
          return setParseError(
            owner === index + 1
              ? `Band ${index + 1}: wallet ${wallet} is listed twice.`
              : `Band ${
                  index + 1
                }: wallet ${wallet} is already in band ${owner}. One wallet, one band.`
          );
        }
        seen.set(wallet, index + 1);
      }
      parsed.push({ coins, title, walletIds: wallets });
    }
    const total = parsed.reduce((sum, band) => sum + band.walletIds.length, 0);
    // The batchId is minted HERE, with the review, and lives until Back or
    // Close: a retry after a lost answer re-sends under the same id, so the
    // server's exactly-once check skips what already landed instead of
    // dropping it twice.
    setConfirming({ bands: parsed, total, batchId: newBatchId() });
  };

  // startAt > 0 = Retry from a failed batch: the earlier batches' answers
  // are kept and only the rest is re-sent (same batchId, see review()).
  const submit = async (startAt = 0) => {
    if (!confirming) return;
    setBusy(true);
    try {
      const chunks = chunkBands(confirming.bands);
      let granted = result?.partial ? result.granted : 0;
      const missing = result?.partial ? [...result.missing] : [];
      const duplicates = result?.partial ? [...result.duplicates] : [];
      for (let index = startAt; index < chunks.length; index += 1) {
        if (chunks.length > 1) setBatchNote(`batch ${index + 1} of ${chunks.length}…`);
        const answer = await onSubmit({ batchId: confirming.batchId, bands: chunks[index] });
        if (!answer) {
          // The batch did not answer. Show what landed so far and keep the
          // review (and its batchId) so Retry resumes from this batch.
          setResult({
            granted,
            missing,
            duplicates,
            partial: true,
            failedBatch: index + 1,
            batches: chunks.length,
          });
          return;
        }
        granted += answer.granted;
        missing.push(...answer.missing);
        duplicates.push(...answer.duplicates);
      }
      setResult({ granted, missing, duplicates });
      setConfirming(null);
    } finally {
      setBusy(false);
      setBatchNote(null);
    }
  };

  return (
    <Modal
      show={show}
      onHide={busy ? undefined : close}
      backdrop={busy ? 'static' : true}
      keyboard={!busy}
      centered
      size="lg"
    >
      <Modal.Header closeButton={!busy}>
        <Modal.Title>
          Grant Jelly <img src={jellyIcon} alt="" className="jelly-icon jelly-icon-lg" />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {result !== null ? (
          <>
            {result.partial && (
              <Alert variant="danger" className="small">
                Batch {result.failedBatch} of {result.batches} did not land. Nothing in it changed.
                Retry re-sends batch {result.failedBatch} onward under the same batch id: wallets
                that already got this drop are skipped, never doubled.
              </Alert>
            )}
            <Alert
              variant={
                result.partial ? 'secondary' : result.missing.length === 0 ? 'success' : 'warning'
              }
              className="small"
            >
              Dropped Jelly to {result.granted} wallet{result.granted === 1 ? '' : 's'}
              {result.partial ? ' so far' : ''}.
              {result.duplicates.length > 0 &&
                ` ${result.duplicates.length} already had this batch and were skipped.`}
              {result.missing.length > 0 &&
                ` ${result.missing.length} matched NO entry. Check them by hand:`}
            </Alert>
            {result.missing.length > 0 && (
              <div className="small font-monospace">{result.missing.join(', ')}</div>
            )}
          </>
        ) : confirming !== null ? (
          <>
            <div className="mb-2">
              Drop{' '}
              {confirming.bands
                .map(
                  band =>
                    `${band.coins} Jelly to ${band.walletIds.length} wallets ("${band.title}")`
                )
                .join(', ')}
              ?
            </div>
            <Alert variant="warning" className="small mb-0">
              This adds to their balance permanently and counts toward every future tier band.
              Titles are shown to players on their task list.
            </Alert>
          </>
        ) : (
          <>
            <div className="text-muted small mb-2">
              Each band drops its own amount to its own wallet list. The title is public: players
              see it on their task list next to the Jelly.
            </div>
            {bands.map((band, index) => (
              <Row className="g-2 mb-2 align-items-start" key={index}>
                <Col md={2}>
                  <Form.Label className="mb-1">
                    Jelly <span className="required-star">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    min={1}
                    max={GRANT_COINS_MAX}
                    value={band.coins}
                    onChange={e => setBand(index, { coins: e.target.value })}
                    aria-label={`band ${index + 1} jelly amount`}
                    disabled={busy}
                  />
                </Col>
                <Col md={4}>
                  <Form.Label className="mb-1">
                    Title players see <span className="required-star">*</span>
                  </Form.Label>
                  <Form.Control
                    placeholder="beta tester thanks"
                    value={band.title}
                    onChange={e => setBand(index, { title: e.target.value })}
                    aria-label={`band ${index + 1} title`}
                    disabled={busy}
                  />
                </Col>
                <Col md={5}>
                  <Form.Label className="mb-1">
                    Wallet list (JSON) <span className="required-star">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    className="font-monospace"
                    placeholder='["wallet1", "wallet2"]'
                    value={band.walletsRaw}
                    onChange={e => setBand(index, { walletsRaw: e.target.value })}
                    aria-label={`band ${index + 1} wallet list JSON`}
                    disabled={busy}
                  />
                </Col>
                <Col md={1}>
                  {bands.length > 1 && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setBands(bands.filter((_, i) => i !== index))}
                      aria-label={`remove band ${index + 1}`}
                      disabled={busy}
                    >
                      ×
                    </Button>
                  )}
                </Col>
              </Row>
            ))}
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setBands([...bands, emptyBand()])}
              disabled={busy}
            >
              Add band
            </Button>
            {parseError && (
              <Alert variant="warning" className="small mt-2 mb-0">
                {parseError}
              </Alert>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {result !== null ? (
          <>
            <Button variant="outline-secondary" size="sm" onClick={close} disabled={busy}>
              Close
            </Button>
            {result.partial && (
              <Button
                variant="jelly"
                size="sm"
                onClick={() => submit(result.failedBatch - 1)}
                disabled={busy}
              >
                {busy ? batchNote ?? 'Dropping…' : `Retry from batch ${result.failedBatch}`}
              </Button>
            )}
          </>
        ) : confirming !== null ? (
          <>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setConfirming(null)}
              disabled={busy}
            >
              Back
            </Button>
            <Button variant="jelly" size="sm" onClick={() => submit()} disabled={busy}>
              {busy
                ? batchNote ?? 'Dropping…'
                : `Confirm: drop Jelly to ${confirming.total} wallets`}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline-secondary" size="sm" onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button variant="jelly" size="sm" onClick={review} disabled={busy}>
              Review drop
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

/** BULK FREEZE (Admin via Gwen, 2026-08-24): his offline screen answers in
 *  wallet lists. Paste the JSON list, give ONE reason, the backend sweeps —
 *  and answers with every wallet it could NOT match, by name.
 *  Lists over 1000 auto-chunk (2026-09-02): the backend caps each sweep at
 *  1000 wallets, and Admin's flag lists can run bigger — paste the whole
 *  thing, the modal runs the batches in order and reports one total. */
const BULK_CHUNK = 1000;

const BulkFreezeModal = ({ show, onSubmit, onHide }) => {
  const [walletsRaw, setWalletsRaw] = useState('');
  const [batchNote, setBatchNote] = useState(null);
  const [reason, setReason] = useState('');
  const [parseError, setParseError] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setWalletsRaw('');
    setReason('');
    setParseError(null);
    setResult(null);
    setBatchNote(null);
  };
  const close = () => {
    reset();
    onHide();
  };

  // startAt > 0 = Retry from a failed batch: the batches before it already
  // froze (and wrote their audit rows), so only the rest is re-sent.
  const submit = async (startAt = 0) => {
    setParseError(null);
    let wallets;
    try {
      wallets = JSON.parse(walletsRaw);
    } catch {
      return setParseError('Not valid JSON. Expected: ["wallet1", "wallet2"]');
    }
    if (
      !Array.isArray(wallets) ||
      wallets.length === 0 ||
      !wallets.every(w => typeof w === 'string')
    ) {
      return setParseError('Expected a JSON list of wallet strings: ["wallet1", "wallet2"]');
    }
    setBusy(true);
    try {
      const batches = Math.ceil(wallets.length / BULK_CHUNK);
      let updated = result?.partial ? result.updated : 0;
      const missing = result?.partial ? [...result.missing] : [];
      for (let index = startAt; index < batches; index += 1) {
        if (batches > 1) setBatchNote(`batch ${index + 1} of ${batches}…`);
        const answer = await onSubmit({
          walletIds: wallets.slice(index * BULK_CHUNK, (index + 1) * BULK_CHUNK),
          reason: reason.trim(),
        });
        if (!answer) {
          // The batch did not answer. Report what froze so far and offer a
          // resume from THIS batch: a whole-list retry would re-freeze the
          // earlier batches and write their audit adjustments twice.
          setResult({ updated, missing, partial: true, failedBatch: index + 1, batches });
          return;
        }
        updated += answer.updated;
        missing.push(...answer.missing);
      }
      setResult({ updated, missing });
    } finally {
      setBusy(false);
      setBatchNote(null);
    }
  };

  return (
    <Modal
      show={show}
      onHide={busy ? undefined : close}
      backdrop={busy ? 'static' : true}
      keyboard={!busy}
      centered
    >
      <Modal.Header closeButton={!busy}>
        <Modal.Title>Bulk Freeze</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {result === null ? (
          <>
            <div className="text-muted small mb-2">
              Paste the wallet list from the screening run as JSON. One shared reason lands in the
              audit trail per wallet. Frozen wallets sit out EVERY wave&apos;s payout until
              unfrozen; unfreezing restores them for the whole waitlist.
            </div>
            <Form.Label className="mb-1" aria-hidden="true">
              Wallet list (JSON) <span className="required-star">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              className="font-monospace"
              placeholder='["wallet1", "wallet2"]'
              value={walletsRaw}
              onChange={e => setWalletsRaw(e.target.value)}
              aria-label="wallet list JSON"
              disabled={busy}
            />
            <Form.Label className="mt-2 mb-1" aria-hidden="true">
              Reason <span className="required-star">*</span>
            </Form.Label>
            <Form.Control
              placeholder="reason, e.g. sybil screen 2026-08-24 cluster 7"
              value={reason}
              onChange={e => setReason(e.target.value)}
              aria-label="bulk freeze reason"
              disabled={busy}
            />
            {parseError && (
              <Alert variant="warning" className="small mt-2 mb-0">
                {parseError}
              </Alert>
            )}
          </>
        ) : (
          <>
            {result.partial && (
              <Alert variant="danger" className="small">
                Batch {result.failedBatch} of {result.batches} did not land. The batches before it
                are frozen and audited; nothing in batch {result.failedBatch} changed. Retry from
                batch {result.failedBatch} re-sends only the remaining wallets, so no earlier freeze
                is written twice.
              </Alert>
            )}
            <Alert
              variant={
                result.partial ? 'secondary' : result.missing.length === 0 ? 'success' : 'warning'
              }
              className="small"
            >
              Froze {result.updated} entr{result.updated === 1 ? 'y' : 'ies'}
              {result.partial ? ' so far' : ''}.
              {result.missing.length > 0 &&
                ` ${result.missing.length} wallet${
                  result.missing.length === 1 ? '' : 's'
                } matched NO entry. Check them by hand:`}
            </Alert>
            {result.missing.length > 0 && (
              <pre className="small mb-0">{result.missing.join('\n')}</pre>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {result === null ? (
          <>
            <Button variant="outline-secondary" size="sm" onClick={close} disabled={busy}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={busy || walletsRaw.trim() === '' || reason.trim() === ''}
              onClick={() => submit()}
            >
              {busy ? batchNote ?? 'Freezing…' : 'Freeze matched wallets'}
            </Button>
          </>
        ) : (
          <>
            {result.partial && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => submit(result.failedBatch - 1)}
                disabled={busy}
              >
                {busy ? batchNote ?? 'Freezing…' : `Retry from batch ${result.failedBatch}`}
              </Button>
            )}
            <Button
              variant={result.partial ? 'outline-secondary' : 'primary'}
              size="sm"
              onClick={close}
              disabled={busy}
            >
              Done
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

/** Ice mark sized like Grant Jelly's `.jelly-icon` (no ice PNG in assets). */
const IceIcon = () => (
  <svg className="jelly-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
    <g fill="none" stroke="currentColor" strokeWidth="1.35" strokeLinecap="round">
      <path d="M10 1.6v16.8M2.7 5.8l14.6 8.4M2.7 14.2l14.6-8.4" />
      <path d="M10 3.4l1.15-1.15M10 3.4L8.85 2.25M10 16.6l1.15 1.15M10 16.6l-1.15 1.15" />
      <path d="M4.15 6.65L3.3 5.2M4.15 6.65L2.7 6.9M15.85 13.35l.85 1.45M15.85 13.35l1.45-.25" />
      <path d="M4.15 13.35L3.3 14.8M4.15 13.35L2.7 13.1M15.85 6.65l.85-1.45M15.85 6.65l1.45.25" />
    </g>
  </svg>
);

const EntriesPanel = ({
  entries,
  onSetEntryState,
  onBulkFreeze,
  onGrantJelly,
  onDownloadCsv,
  onDownloadWalletsCsv,
  pulseAntiSybil = false,
  // Exports ride /admin/export, outside the service's mint-only routes.
  canExport = true,
}) => {
  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [codeFilter, setCodeFilter] = useState('');
  const [pending, setPending] = useState(null); // { cognitoSub, targetState }
  const [showBulk, setShowBulk] = useState(false);
  const mutable = typeof onSetEntryState === 'function' && typeof onBulkFreeze === 'function';
  const grantable = typeof onGrantJelly === 'function';
  const [showGrant, setShowGrant] = useState(false);
  const [exportingCount, setExportingCount] = useState(false);

  const codesInUse = useMemo(() => [...new Set(entries.map(e => e.code))].sort(), [entries]);

  const shown = useMemo(() => {
    const needle = search.trim().toUpperCase();
    return entries.filter(e => {
      if (stateFilter && e.state !== stateFilter) return false;
      if (codeFilter && e.code !== codeFilter) return false;
      if (
        needle &&
        !e.code.includes(needle) &&
        !(e.walletId ?? '').toUpperCase().includes(needle) &&
        !(e.cognitoSub ?? '').toUpperCase().includes(needle)
      )
        return false;
      return true;
    });
  }, [entries, search, stateFilter, codeFilter]);

  return (
    <div className="waitlist-panel">
      <h5>Entries</h5>
      <div className="text-muted small mb-2">
        The sybil review works here. The CSV carries Admin&apos;s offline pipeline: attribution,
        checkmarks, coins, fruit, fingerprint hashes.
      </div>
      <Row className="g-2 mb-2 waitlist-entries-filters">
        <Col md={5}>
          <Form.Control
            placeholder="search wallet / code / account…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Form.Select
            aria-label="state filter"
            value={stateFilter}
            onChange={e => setStateFilter(e.target.value)}
          >
            <option value="">all states</option>
            {Object.keys(STATE_BADGE).map(s => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Select
            aria-label="code filter"
            value={codeFilter}
            onChange={e => setCodeFilter(e.target.value)}
          >
            <option value="">all codes</option>
            {codesInUse.map(c => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>
      <Row className="g-2 mb-2 waitlist-entries-actions align-items-center">
        {canExport && (
          <Col xs="auto">
            <Button variant="outline-secondary" onClick={onDownloadCsv}>
              Download CSV
            </Button>
          </Col>
        )}
        {canExport && (
          <Col xs="auto">
            <Button
              variant="outline-secondary"
              className={pulseAntiSybil ? 'waitlist-antisybil-pulse' : undefined}
              disabled={exportingCount !== false}
              onClick={async () => {
                setExportingCount(0);
                try {
                  await onDownloadWalletsCsv(count => setExportingCount(count ?? 0));
                } finally {
                  setExportingCount(false);
                }
              }}
            >
              {exportingCount === false
                ? 'Anti-Sybil Report'
                : exportingCount > 0
                ? `Exporting… ${exportingCount.toLocaleString()} wallets`
                : 'Exporting…'}
            </Button>
          </Col>
        )}
        {mutable && (
          <Col xs="auto">
            <Button variant="freeze" onClick={() => setShowBulk(true)}>
              Bulk Freeze <IceIcon />
            </Button>
          </Col>
        )}
        {grantable && (
          <Col xs="auto">
            <Button variant="jelly" onClick={() => setShowGrant(true)}>
              Grant Jelly <img src={jellyIcon} alt="" className="jelly-icon" />
            </Button>
          </Col>
        )}
      </Row>
      <Table hover responsive size="sm">
        <thead>
          <tr>
            <th>Account</th>
            <th>Wallet</th>
            <th>Code</th>
            <th>Wave</th>
            <th>Jelly</th>
            <th>Fruit</th>
            <th>Entered (UTC)</th>
            <th>State</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {shown.map(e => (
            <tr key={e.cognitoSub}>
              <td className="text-muted">
                <CopyShort value={e.cognitoSub} label="account" />
              </td>
              <td className="text-muted">
                <CopyShort value={e.walletId} label="wallet" />
              </td>
              <td className="fw-semibold">{e.code}</td>
              <td className="text-muted">{e.waveName ?? '—'}</td>
              <td>{e.coins}</td>
              <td>{e.fruit ?? ''}</td>
              <td className="text-muted">{showUtcClock(e.enteredAt)}</td>
              <td>
                <Badge bg={STATE_BADGE[e.state] ?? 'secondary'}>{e.state}</Badge>{' '}
                {e.state === 'frozen' && (
                  <Badge bg="warning" text="dark">
                    waves paused
                  </Badge>
                )}
              </td>
              <td>
                {mutable && e.state === 'active' && (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setPending({ cognitoSub: e.cognitoSub, targetState: 'frozen' })}
                  >
                    Freeze
                  </Button>
                )}
                {mutable && e.state === 'frozen' && (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => setPending({ cognitoSub: e.cognitoSub, targetState: 'active' })}
                  >
                    Unfreeze
                  </Button>
                )}
              </td>
            </tr>
          ))}
          {shown.length === 0 && (
            <tr>
              <td colSpan={9} className="text-center text-muted py-4">
                No entries match.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      {mutable && (
        <>
          <BulkFreezeModal
            show={showBulk}
            onSubmit={onBulkFreeze}
            onHide={() => setShowBulk(false)}
          />
          {grantable && (
            <GrantJellyModal
              show={showGrant}
              onSubmit={onGrantJelly}
              onHide={() => setShowGrant(false)}
            />
          )}
          <ReasonModal
            show={pending !== null}
            targetState={pending?.targetState}
            targetLabel={short(pending?.cognitoSub ?? '')}
            onConfirm={reason =>
              onSetEntryState({
                cognitoSub: pending.cognitoSub,
                state: pending.targetState,
                reason,
              })
            }
            onHide={() => setPending(null)}
          />
        </>
      )}
    </div>
  );
};

export default EntriesPanel;
